import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface UserProfile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  current_age: number | null;
  monthly_income: number | null;
  city_type: string | null;
  total_monthly_emis: number | null;
  current_savings: number | null;
  primary_goal: string | null;
  total_investments: number | null;
  monthly_sip: number | null;
  expected_return_rate: number | null;
  tax_saving_investments: number | null;
  risk_appetite: string | null;
  goal_targets: Record<string, number> | null;
}

interface ProfileContextType {
  profile: UserProfile | null;
  loading: boolean;
  updateProfile: (data: Partial<UserProfile>) => Promise<boolean>;
  refetch: () => void;
}

const ProfileContext = createContext<ProfileContextType>({
  profile: null,
  loading: true,
  updateProfile: async () => false,
  refetch: () => {},
});

export const useProfile = () => useContext(ProfileContext);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setProfile(data as UserProfile | null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return false;
    const { error } = await supabase
      .from("profiles")
      .update(updates as any)
      .eq("user_id", user.id);
    if (!error) {
      await fetchProfile();
      return true;
    }
    return false;
  };

  return (
    <ProfileContext.Provider value={{ profile, loading, updateProfile, refetch: fetchProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}
